import CallRecordsResource from 'flow/api/callrecords';
import Actions from 'flow/actions';
import moment from 'moment';
import { QueueKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { CallRecord } from 'flow/types/CallRecord';
import { AppDispatch } from 'flow/types/Redux';
import { State } from 'flow/types/State';
import { networkErrorDispatcher } from 'flow/actions/app-actions';
import {
    selectOwnExtensionKey,
    selectContact,
} from 'flow/selectors/ExtensionSelectors';
import { getCallRecordActionMarkedAsHandled } from 'flow/utils/call-records-utils';
import { ISO_8601_MS_RESOLUTION } from 'flow/utils';

const ACTION_HANDLE_UNANSWERED = 'handle-unanswered';

const markForQueue =
    (callRecord: TypedMap<CallRecord>, queueKey: QueueKey) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const state = getState();
        const callRecordKey = callRecord.get('key');
        const ownExtensionKey = selectOwnExtensionKey(state);
        const contact = selectContact(state, ownExtensionKey);
        const bPartNumber = callRecord.getIn(['number', 'number']);

        const callActionDTO = {
            agent: { key: ownExtensionKey },
            name: ACTION_HANDLE_UNANSWERED,
            timestamp: moment().format(ISO_8601_MS_RESOLUTION),
        };

        CallRecordsResource.createCallAction(
            {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onResult(json: any) {
                    json.agent.contact = contact;
                    dispatch({
                        type: Actions.RESULT_CREATE_CALL_ACTION,
                        callRecordKey,
                        queueKey,
                        bPartNumber,
                        data: json,
                    });
                },
                onError: networkErrorDispatcher(dispatch),
            },
            callRecordKey,
            callActionDTO,
        );
    };

const markForPersonalHistory =
    (callRecord: TypedMap<CallRecord>) => (dispatch: AppDispatch) => {
        const callRecordKey = callRecord.get('key');

        const callActionDTO = {
            name: ACTION_HANDLE_UNANSWERED,
            timestamp: moment().format(ISO_8601_MS_RESOLUTION),
        };

        CallRecordsResource.createCallAction(
            {
                onResult(json: unknown) {
                    dispatch({
                        type: Actions.RESULT_CREATE_CALL_ACTION_PERSONAL_CALL,
                        callRecordKey,
                        data: json,
                    });
                },
                onError: networkErrorDispatcher(dispatch),
            },
            callRecordKey,
            callActionDTO,
        );
    };

export const markCallRecordHandled =
    (callRecord: TypedMap<CallRecord>, queueKey?: QueueKey) =>
    (dispatch: AppDispatch) => {
        if (queueKey) {
            dispatch(markForQueue(callRecord, queueKey));
        } else {
            dispatch(markForPersonalHistory(callRecord));
        }
    };

export const unmarkCallRecordHandled =
    (callRecord: TypedMap<CallRecord>, queueKey?: QueueKey) =>
    (dispatch: AppDispatch) => {
        const callRecordKey = callRecord.get('key');
        const callAction = getCallRecordActionMarkedAsHandled(callRecord);
        const callActionKey = callAction.get('key');

        CallRecordsResource.deleteCallAction(
            {
                onResult() {
                    dispatch({
                        type: queueKey
                            ? Actions.RESULT_DELETE_CALL_ACTION
                            : Actions.RESULT_DELETE_CALL_ACTION_PERSONAL_CALL,
                        callRecordKey,
                        callActionKey,
                        ...(queueKey && {
                            queueKey,
                            bPartNumber: callRecord.getIn(['number', 'number']),
                            // bPartNumber is only used by the pbx-reducer for queue-specific paths today,
                            // but could be relevant for personal history (recordsPerNumber) in the future.
                        }),
                    });
                },
                onError: networkErrorDispatcher(dispatch),
            },
            callRecordKey,
            callActionKey,
        );
    };
